import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import clsx from "clsx";

import { BillingCard } from "../components/profile/BillingCard";
import { CustomVoicesCard } from "../components/profile/CustomVoicesCard";
import { SnippetCard } from "../components/profile/SnippetCard";
import { Button, Text, View, Container } from "../components/base";
import { Dialog } from "../components/base/Dialog";
import { Icon } from "../components/base/Icon";
import { InputField } from "../components/base/InputField";
import { organizationActions } from "../redux/reducers/organizationSlice";
import { MembersCard } from "../components/profile/MembersCard";
import { organizationInvitesActions } from "../redux/reducers/organizationInvitesSlice";
import { organizationMembersActions } from "../redux/reducers/organizationMemberSlice";
import { organizationTelephonyActions, OrganizationTelephony } from "../redux/reducers/organizationTelephonySlice";
import { OrganizationVariable, organizationVariablesActions } from "../redux/reducers/organizationVariablesSlice";
import { organizationCustomVoicesActions } from "../redux/reducers/organizationCustomVoicesSlice";
import { organizationSnippetActions, OrganizationSnippet } from "../redux/reducers/organizationSnippet";
import { STRIPE_PUBLISHABLE_KEY } from "../config";
import { TelephonyCard } from "../components/profile/TelephonyCard";
import { useApiRequest } from "../hooks/useApiRequest";
import { useAppDispatch, useAppSelector } from "../redux";
import { useEffectiveOrganization } from "../hooks/useEffectiveOrganization";
import { GlobalVariablesCard } from "../components/profile/GlobalVariablesCard";

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

type Tab = {
  id: string;
  label: string;
  icon: string;
};

const baseTabs: Tab[] = [
  { id: "members", label: "Members", icon: "users" },
  { id: "telephony", label: "Telephony", icon: "phone" },
  { id: "variables", label: "Global Variables", icon: "list" },
  { id: "custom-voices", label: "Custom Voices", icon: "mic" },
  { id: "snippets", label: "Snippets", icon: "snippet" },
  { id: "usage", label: "Usage", icon: "activity" },
];

const filterEntities = <T extends { organizationId: string }>(entities: Record<string, T>, organizationId = "") => {
  return Object.values(entities).filter((entity) => entity.organizationId === organizationId || !entity.organizationId);
};

export const OrganizationPage = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const user = useAppSelector((state) => state.session.user);

  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [amount, setAmount] = useState<number>(10);
  const [isCustomAmount, setIsCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [customAmountError, setCustomAmountError] = useState<string>("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const createPaymentRequest = useApiRequest<{ sessionId: string }>();
  const [isPlayingVoiceId, setIsPlayingVoiceId] = useState("");
  const currentPlayingVoice = useRef<HTMLAudioElement | null>(null);
  const { organizationId: effectiveOrganizationId } = useEffectiveOrganization();

  const organizationVariables = useAppSelector((state) => filterEntities(state.organizationVariables.entities, effectiveOrganizationId));
  const organizationMembers = useAppSelector((state) => filterEntities(state.organizationMembers.entities, effectiveOrganizationId));
  const organizationInvites = useAppSelector((state) => filterEntities(state.organizationInvites.entities, effectiveOrganizationId));
  const organizationTelephony = useAppSelector((state) => filterEntities(state.organizationTelephony.entities, effectiveOrganizationId));
  const organizationTelephonyIsLoading = useAppSelector((state) => state.organizationTelephony.isLoading);
  const organizationCustomVoices = useAppSelector((state) => filterEntities(state.organizationCustomVoices.entities, effectiveOrganizationId));
  const organizationCustomVoicesIsLoading = useAppSelector((state) => state.organizationCustomVoices.isLoading);
  const organizationSnippets = useAppSelector((state) => filterEntities(state.organizationSnippets.entities, effectiveOrganizationId));
  const organizationSnippetsIsLoading = useAppSelector((state) => state.organizationSnippets.isLoading);

  // Filter tabs based on user role - show billing to system admins and organization admins
  const currentMember = organizationMembers.find((member) => member.userId === user?.id);
  const isOrganizationAdmin = currentMember?.role === "admin" || currentMember?.role === "owner";

  const tabs = baseTabs.filter((tab) => {
    if (tab.id === "usage") {
      return user?.isAdmin === true || isOrganizationAdmin;
    }
    return true;
  });

  const [activeTab, setActiveTab] = useState(() => {
    // Initialize from URL hash or default to 'organization'
    const hash = window.location.hash.slice(1);
    return tabs.some((tab) => tab.id === hash) ? hash : "members";
  });

  // Update URL hash when active tab changes
  useEffect(() => {
    window.location.hash = activeTab;
  }, [activeTab]);

  // Listen for hash changes (via React Router)
  useEffect(() => {
    const hash = location.hash.slice(1);
    if (tabs.some((tab) => tab.id === hash)) {
      setActiveTab(hash);
    }
  }, [location.hash]);

  const onClickInviteUser = (email: string, role: "admin" | "member") => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationInvitesActions.create({
          organizationId: effectiveOrganizationId,
          email,
          role,
        }),
      );
    }
  };

  const onClickCancelInvite = (id: string) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationInvitesActions.deleteById({
          organizationId: effectiveOrganizationId,
          id,
        }),
      );
    }
  };

  const onClickUpdateMemberRole = (id: string, role: "admin" | "member") => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationMembersActions.updateById({
          organizationId: effectiveOrganizationId,
          id,
          role,
        }),
      );
    }
  };

  const onClickRemoveMember = (memberId: string) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationMembersActions.deleteById({
          organizationId: effectiveOrganizationId,
          id: memberId,
        }),
      );
    }
  };

  const onClickPurchaseAmount = async () => {
    try {
      const stripe = await stripePromise;
      if (!stripe) throw new Error("Stripe failed to load");

      const response = await createPaymentRequest.fetch("/v1/payments/create-session", {
        method: "POST",
        body: { amount },
      });

      const result = await stripe.redirectToCheckout({
        sessionId: response.data?.sessionId || "",
      });

      if (result.error) {
        console.error(result.error);
      }
    } catch (err) {
      console.error("Payment error:", err);
    }
  };

  // Manage voice audio event listeners
  useEffect(() => {
    if (!currentPlayingVoice.current || !isPlayingVoiceId) return;

    const audio = currentPlayingVoice.current;
    const handleEnded = () => setIsPlayingVoiceId("");
    const handlePause = () => setIsPlayingVoiceId("");
    const handleError = () => {
      setIsPlayingVoiceId("");
      currentPlayingVoice.current = null;
    };

    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("error", handleError);
    };
  }, [isPlayingVoiceId]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentPlayingVoice.current) {
        currentPlayingVoice.current.pause();
        currentPlayingVoice.current = null;
        setIsPlayingVoiceId("");
      }
    };
  }, []);

  const onChangeOrganizationVariables = (updatedVariable: Partial<OrganizationVariable>) => {
    dispatch(
      organizationVariablesActions.updateById({
        ...updatedVariable,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  const onClickAddOrganizationVariable = () => {
    dispatch(
      organizationVariablesActions.create({
        name: "",
        value: "",
        masked: false,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  const onClickDeleteOrganizationVariable = async (id: string) => {
    await dispatch(
      organizationVariablesActions.deleteById({
        id,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  const onClickAddTelephony = (config: Partial<OrganizationTelephony>) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationTelephonyActions.create({
          ...config,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  const onClickUpdateTelephony = (config: Partial<OrganizationTelephony>) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationTelephonyActions.updateById({
          ...config,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  const onClickDeleteTelephony = (id: string) => {
    if (effectiveOrganizationId) {
      dispatch(
        organizationTelephonyActions.deleteById({
          id,
          organizationId: effectiveOrganizationId,
        }),
      );
    }
  };

  // Custom voices handlers
  const handleCreateVoice = async (params: { name: string; description: string; language: string; file: File }) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationCustomVoicesActions.createVoiceClone({
        organizationId: effectiveOrganizationId,
        name: params.name,
        description: params.description,
        language: params.language,
        file: params.file,
      }),
    );
  };

  const handleUpdateVoice = async (id: string, params: { name: string; description: string }) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationCustomVoicesActions.updateById({
        id,
        organizationId: effectiveOrganizationId,
        name: params.name,
        description: params.description,
      }),
    );
  };

  const handleDeleteVoice = async (voiceId: string) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationCustomVoicesActions.deleteById({
        id: voiceId,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  // Audio snippet handlers
  const handleCreateSnippet = (snippet: OrganizationSnippet) => {
    // Snippet is already created via the dialog, so we just need to refresh
    if (effectiveOrganizationId) {
      dispatch(organizationSnippetActions.get({ organizationId: effectiveOrganizationId }));
    }
  };

  const handleUpdateSnippet = async (id: string, params: { name: string }) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationSnippetActions.updateById({
        id,
        organizationId: effectiveOrganizationId,
        name: params.name,
      }),
    );
  };

  const handleDeleteSnippet = async (id: string) => {
    if (!effectiveOrganizationId) return;

    await dispatch(
      organizationSnippetActions.deleteById({
        id,
        organizationId: effectiveOrganizationId,
      }),
    );
  };

  useEffect(() => {
    if (effectiveOrganizationId) {
      dispatch(organizationActions.getById({ id: effectiveOrganizationId }));
      dispatch(organizationVariablesActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationMembersActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationInvitesActions.get({ organizationId: effectiveOrganizationId, status: "pending" }));
      dispatch(organizationTelephonyActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationCustomVoicesActions.get({ organizationId: effectiveOrganizationId }));
      dispatch(organizationSnippetActions.get({ organizationId: effectiveOrganizationId }));
    }
  }, [effectiveOrganizationId]);

  const renderTabContent = () => {
    switch (activeTab) {
      case "members":
        return (
          <MembersCard
            members={organizationMembers}
            invites={organizationInvites.filter((invite) => invite.status === "pending")}
            onInviteUser={onClickInviteUser}
            onCancelInvite={onClickCancelInvite}
            onUpdateMemberRole={onClickUpdateMemberRole}
            onRemoveMember={onClickRemoveMember}
          />
        );
      case "variables":
        return (
          <GlobalVariablesCard
            variables={organizationVariables}
            onChange={onChangeOrganizationVariables}
            onClickAdd={onClickAddOrganizationVariable}
            onClickDelete={onClickDeleteOrganizationVariable}
          />
        );
      case "telephony":
        return (
          <TelephonyCard
            telephonyConfigs={organizationTelephony}
            onAdd={onClickAddTelephony}
            onUpdate={onClickUpdateTelephony}
            onDelete={onClickDeleteTelephony}
            isLoading={organizationTelephonyIsLoading}
          />
        );
      case "custom-voices":
        return (
          <CustomVoicesCard
            customVoices={organizationCustomVoices}
            isLoading={organizationCustomVoicesIsLoading}
            onCreateVoice={handleCreateVoice}
            onUpdateVoice={handleUpdateVoice}
            onDeleteVoice={handleDeleteVoice}
          />
        );
      case "snippets":
        return (
          <SnippetCard
            snippets={organizationSnippets}
            isLoading={organizationSnippetsIsLoading}
            organizationId={effectiveOrganizationId || ""}
            onCreateSnippet={handleCreateSnippet}
            onUpdateSnippet={handleUpdateSnippet}
            onDeleteSnippet={handleDeleteSnippet}
          />
        );
      case "usage":
        return <BillingCard />;
      default:
        return null;
    }
  };

  return (
    <div className="w-full min-h-full bg-white dark:bg-slate-900">
      <Container
        size="xl"
        className="py-8 min-h-full"
      >
        {/* Mobile Tab Menu */}
        <div className="sm:hidden mb-6">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="flex items-center justify-between w-full p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
          >
            <div className="flex items-center">
              <Icon
                name={tabs.find((tab) => tab.id === activeTab)?.icon || "users"}
                size={20}
                className="mr-3"
              />
              <Text className="font-medium">{tabs.find((tab) => tab.id === activeTab)?.label || "Organization"}</Text>
            </div>
            <Icon
              name={isMobileMenuOpen ? "chevron-up" : "chevron-down"}
              size={20}
            />
          </button>

          {isMobileMenuOpen && (
            <div className="mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id);
                    setIsMobileMenuOpen(false);
                  }}
                  className={clsx(
                    "w-full flex items-center px-3 py-3 text-left border-b border-gray-100 dark:border-gray-700 last:border-b-0",
                    activeTab === tab.id ? "bg-brand/10 text-brand" : "hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300",
                  )}
                >
                  <Icon
                    name={tab.icon}
                    size={20}
                    className="mr-3"
                  />
                  {tab.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Desktop Tab Navigation */}
        <div className="hidden sm:block border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={clsx(
                  "flex items-center px-1 py-4 text-sm font-medium border-b-2 whitespace-nowrap",
                  activeTab === tab.id
                    ? "border-brand text-brand dark:text-brand-light"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300",
                )}
              >
                <Icon
                  name={tab.icon}
                  size={20}
                  className="mr-2"
                />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        {/* Main Content */}
        <div>{renderTabContent()}</div>
      </Container>

      <Dialog
        isOpen={showPurchaseDialog}
        onClose={() => setShowPurchaseDialog(false)}
        title="Add Funds"
      >
        <View className="p-4 space-y-4">
          <Text>Select Amount:</Text>
          <View className="grid grid-cols-2 gap-2">
            <Button
              onClick={() => {
                setAmount(5);
                setIsCustomAmount(false);
                setCustomAmount("");
              }}
              variant="outline"
              className={`w-full ${amount === 5 && !isCustomAmount ? "bg-brand text-white" : ""}`}
            >
              $5
            </Button>
            <Button
              onClick={() => {
                setAmount(10);
                setIsCustomAmount(false);
                setCustomAmount("");
              }}
              variant="outline"
              className={`w-full ${amount === 10 && !isCustomAmount ? "bg-brand text-white" : ""}`}
            >
              $10
            </Button>
            <Button
              onClick={() => {
                setAmount(25);
                setIsCustomAmount(false);
                setCustomAmount("");
              }}
              variant="outline"
              className={`w-full ${amount === 25 && !isCustomAmount ? "bg-brand text-white" : ""}`}
            >
              $25
            </Button>
            <Button
              onClick={() => {
                setAmount(50);
                setIsCustomAmount(false);
                setCustomAmount("");
              }}
              variant="outline"
              className={`w-full ${amount === 50 && !isCustomAmount ? "bg-brand text-white" : ""}`}
            >
              $50
            </Button>
          </View>

          <Button
            onClick={() => {
              setIsCustomAmount(true);
              setAmount(0);
            }}
            variant="outline"
            className={`w-full ${isCustomAmount ? "bg-brand text-white" : ""}`}
          >
            Enter Custom Amount
          </Button>

          {isCustomAmount && (
            <View className="space-y-2">
              <Text>Enter custom amount (USD):</Text>
              <View className="relative">
                <Text className="absolute left-3 top-1/2 -translate-y-1/2">$</Text>
                <InputField
                  type="number"
                  value={customAmount}
                  onChange={(value) => {
                    setCustomAmount(value);
                    const numValue = Number(value) || 0;
                    if (numValue < 5 && value !== "") {
                      setCustomAmountError("Minimum amount is $5");
                      setAmount(0);
                    } else {
                      setCustomAmountError("");
                      setAmount(numValue);
                    }
                  }}
                  className={`w-full pl-7 ${customAmountError ? "border-red-500 dark:border-red-400" : ""}`}
                  placeholder="0.00"
                />
              </View>
              {customAmountError && <Text className="text-red-500 dark:text-red-400 text-sm">{customAmountError}</Text>}
            </View>
          )}

          <Button
            onClick={onClickPurchaseAmount}
            isDisabled={amount < 5 || createPaymentRequest.isLoading}
            className="bg-brand hover:bg-brand-dark disabled:bg-brand/50 text-white px-4 py-2 w-full"
          >
            {createPaymentRequest.isLoading ? "Processing..." : `Add $${amount === 0 ? "0.00" : amount.toFixed(2)}`}
          </Button>
        </View>
      </Dialog>
    </div>
  );
};
